import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Package, MapPin, LogOut, ChevronRight, Edit2, Loader2, Plus, Trash2, Star } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useCustomer } from '@/context/CustomerContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerAccountPage = () => {
  const navigate = useNavigate();
  const { customer, logout, updateProfile, getOrders, isAuthenticated, loading: authLoading, token } = useCustomer();
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [defaultAddressId, setDefaultAddressId] = useState(null);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [savingAddress, setSavingAddress] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: ''
  });
  const [addressForm, setAddressForm] = useState({
    label: 'Home',
    name: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    is_default: false
  });

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/account' } } });
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (customer) {
      setFormData({
        name: customer.name || '',
        phone: customer.phone || ''
      });
    }
  }, [customer]);

  useEffect(() => {
    const fetchData = async () => {
      if (isAuthenticated && token) {
        // Fetch orders
        try {
          const data = await getOrders();
          setOrders(data);
        } catch (error) {
          console.error('Error fetching orders:', error);
        } finally {
          setLoadingOrders(false);
        }

        // Fetch addresses
        try {
          const response = await axios.get(`${API}/customer/addresses`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setAddresses(response.data.addresses || []);
          setDefaultAddressId(response.data.default_address_id);
        } catch (error) {
          console.error('Error fetching addresses:', error);
        } finally {
          setLoadingAddresses(false);
        }
      }
    };
    fetchData();
  }, [isAuthenticated, token, getOrders]);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleAddressChange = (e) => {
    setAddressForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await updateProfile(formData);
      toast.success('Profile updated successfully');
      setEditing(false);
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const openAddressDialog = (address = null) => {
    if (address) {
      setEditingAddress(address);
      setAddressForm({
        label: address.label || 'Home',
        name: address.name || '',
        phone: address.phone || '',
        address: address.address || '',
        city: address.city || '',
        state: address.state || '',
        pincode: address.pincode || '',
        is_default: address.id === defaultAddressId
      });
    } else {
      setEditingAddress(null);
      setAddressForm({
        label: 'Home',
        name: customer?.name || '',
        phone: customer?.phone || '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        is_default: addresses.length === 0
      });
    }
    setAddressDialogOpen(true);
  };

  const handleSaveAddress = async () => {
    if (!addressForm.name || !addressForm.phone || !addressForm.address || !addressForm.city || !addressForm.state || !addressForm.pincode) {
      toast.error('Please fill all required fields');
      return;
    }

    setSavingAddress(true);
    try {
      if (editingAddress) {
        // Update existing address
        await axios.put(`${API}/customer/addresses/${editingAddress.id}`, addressForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAddresses(prev => prev.map(addr => 
          addr.id === editingAddress.id 
            ? { ...addr, ...addressForm }
            : addr
        ));
        if (addressForm.is_default) {
          setDefaultAddressId(editingAddress.id);
        }
        toast.success('Address updated successfully');
      } else {
        // Add new address
        const response = await axios.post(`${API}/customer/addresses`, addressForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAddresses(prev => [...prev, response.data.address]);
        if (addressForm.is_default || addresses.length === 0) {
          setDefaultAddressId(response.data.address.id);
        }
        toast.success('Address added successfully');
      }
      setAddressDialogOpen(false);
    } catch (error) {
      toast.error('Failed to save address');
    } finally {
      setSavingAddress(false);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;

    try {
      await axios.delete(`${API}/customer/addresses/${addressId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddresses(prev => prev.filter(addr => addr.id !== addressId));
      if (defaultAddressId === addressId) {
        const remaining = addresses.filter(addr => addr.id !== addressId);
        setDefaultAddressId(remaining.length > 0 ? remaining[0].id : null);
      }
      toast.success('Address deleted');
    } catch (error) {
      toast.error('Failed to delete address');
    }
  };

  const handleSetDefault = async (addressId) => {
    try {
      await axios.put(`${API}/customer/addresses/${addressId}/default`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDefaultAddressId(addressId);
      toast.success('Default address updated');
    } catch (error) {
      toast.error('Failed to update default address');
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      pending: 'status-pending',
      confirmed: 'status-confirmed',
      processing: 'status-processing',
      shipped: 'status-shipped',
      delivered: 'status-delivered',
      cancelled: 'status-cancelled'
    };
    return classes[status] || 'status-pending';
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loader" />
      </div>
    );
  }

  if (!customer) return null;

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="bg-white py-12 border-b border-sand">
        <div className="container-handcraft">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to="/" className="hover:text-charcoal transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-charcoal">My Account</span>
          </nav>

          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center">
                <User className="w-8 h-8 text-accent" />
              </div>
              <div>
                <p className="font-handwritten text-xl text-accent">Welcome back</p>
                <h1 className="font-serif text-3xl text-charcoal">{customer.name}</h1>
              </div>
            </div>
            <Button 
              variant="outline" 
              onClick={handleLogout}
              className="text-muted-foreground"
              data-testid="customer-logout"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-spacing">
        <div className="container-handcraft">
          <Tabs defaultValue="orders" className="space-y-8">
            <TabsList className="bg-white border border-sand">
              <TabsTrigger value="orders" className="data-[state=active]:bg-charcoal data-[state=active]:text-cream">
                <Package className="w-4 h-4 mr-2" />
                My Orders
              </TabsTrigger>
              <TabsTrigger value="addresses" className="data-[state=active]:bg-charcoal data-[state=active]:text-cream">
                <MapPin className="w-4 h-4 mr-2" />
                Addresses
              </TabsTrigger>
              <TabsTrigger value="profile" className="data-[state=active]:bg-charcoal data-[state=active]:text-cream">
                <User className="w-4 h-4 mr-2" />
                Profile
              </TabsTrigger>
            </TabsList>

            {/* Orders Tab */}
            <TabsContent value="orders">
              <Card>
                <CardHeader>
                  <CardTitle className="font-serif text-xl">Order History</CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingOrders ? (
                    <div className="space-y-4">
                      {[...Array(3)].map((_, i) => (
                        <div key={i} className="h-24 skeleton rounded-xl" />
                      ))}
                    </div>
                  ) : orders.length === 0 ? (
                    <div className="text-center py-12">
                      <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground mb-4">You haven't placed any orders yet</p>
                      <Link to="/shop">
                        <Button className="btn-handcraft bg-charcoal text-cream">
                          Start Shopping
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {orders.map((order, index) => (
                        <motion.div
                          key={order.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="bg-muted/30 rounded-xl p-4 md:p-6"
                          data-testid={`order-${order.id}`}
                        >
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                            <div>
                              <div className="flex items-center gap-3 mb-1">
                                <h3 className="font-medium text-charcoal">{order.order_number}</h3>
                                <span className={`badge-handcraft ${getStatusBadgeClass(order.status)}`}>
                                  {order.status}
                                </span>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {new Date(order.created_at).toLocaleDateString('en-IN', {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric'
                                })}
                              </p>
                            </div>
                            <p className="font-semibold text-lg">₹{order.total?.toLocaleString()}</p>
                          </div>

                          <div className="flex flex-wrap gap-2">
                            {order.items?.slice(0, 3).map((item, i) => (
                              <div key={i} className="flex items-center gap-2 bg-white rounded-lg p-2 pr-4">
                                <div className="w-10 h-10 rounded-lg overflow-hidden bg-muted">
                                  {item.product_image && (
                                    <img src={item.product_image} alt="" className="w-full h-full object-cover" />
                                  )}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-charcoal line-clamp-1">{item.product_name}</p>
                                  <p className="text-xs text-muted-foreground">x{item.quantity}</p>
                                </div>
                              </div>
                            ))}
                            {order.items?.length > 3 && (
                              <div className="flex items-center px-3 text-sm text-muted-foreground">
                                +{order.items.length - 3} more
                              </div>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Addresses Tab */}
            <TabsContent value="addresses">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-serif text-xl">Saved Addresses</CardTitle>
                  <Button 
                    onClick={() => openAddressDialog()}
                    className="bg-charcoal text-cream hover:bg-charcoal/90"
                    data-testid="add-address-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Address
                  </Button>
                </CardHeader>
                <CardContent>
                  {loadingAddresses ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[...Array(2)].map((_, i) => (
                        <div key={i} className="h-40 skeleton rounded-xl" />
                      ))}
                    </div>
                  ) : addresses.length === 0 ? (
                    <div className="text-center py-12">
                      <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground mb-4">No saved addresses</p>
                      <Button 
                        onClick={() => openAddressDialog()}
                        className="bg-charcoal text-cream"
                      >
                        Add Your First Address
                      </Button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {addresses.map((address, index) => (
                        <motion.div
                          key={address.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className={`relative p-4 rounded-xl border-2 ${
                            address.id === defaultAddressId 
                              ? 'border-accent bg-accent/5' 
                              : 'border-sand bg-white'
                          }`}
                          data-testid={`address-card-${address.id}`}
                        >
                          {address.id === defaultAddressId && (
                            <span className="absolute top-3 right-3 badge-handcraft bg-accent text-white text-xs">
                              Default
                            </span>
                          )}
                          <div className="flex items-start gap-3 mb-3">
                            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                              <MapPin className="w-5 h-5 text-muted-foreground" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-charcoal">{address.name}</span>
                                <span className="badge-handcraft bg-muted text-muted-foreground text-xs">
                                  {address.label}
                                </span>
                              </div>
                              <p className="text-sm text-muted-foreground">{address.phone}</p>
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground mb-4">
                            {address.address}, {address.city}, {address.state} - {address.pincode}
                          </p>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openAddressDialog(address)}
                              data-testid={`edit-address-${address.id}`}
                            >
                              <Edit2 className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                            {address.id !== defaultAddressId && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleSetDefault(address.id)}
                                data-testid={`set-default-${address.id}`}
                              >
                                <Star className="w-3 h-3 mr-1" />
                                Set Default
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteAddress(address.id)}
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-address-${address.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Address Dialog */}
              <Dialog open={addressDialogOpen} onOpenChange={setAddressDialogOpen}>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-xl">
                      {editingAddress ? 'Edit Address' : 'Add New Address'}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="label-handcraft">Full Name *</Label>
                        <Input
                          name="name"
                          value={addressForm.name}
                          onChange={handleAddressChange}
                          placeholder="John Doe"
                          data-testid="dialog-address-name"
                        />
                      </div>
                      <div>
                        <Label className="label-handcraft">Phone *</Label>
                        <Input
                          name="phone"
                          value={addressForm.phone}
                          onChange={handleAddressChange}
                          placeholder="+91 98765 43210"
                          data-testid="dialog-address-phone"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="label-handcraft">Address *</Label>
                      <Textarea
                        name="address"
                        value={addressForm.address}
                        onChange={handleAddressChange}
                        placeholder="House/Flat No., Building, Street"
                        data-testid="dialog-address-address"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="label-handcraft">City *</Label>
                        <Input
                          name="city"
                          value={addressForm.city}
                          onChange={handleAddressChange}
                          placeholder="Mumbai"
                          data-testid="dialog-address-city"
                        />
                      </div>
                      <div>
                        <Label className="label-handcraft">State *</Label>
                        <Input
                          name="state"
                          value={addressForm.state}
                          onChange={handleAddressChange}
                          placeholder="Maharashtra"
                          data-testid="dialog-address-state"
                        />
                      </div>
                      <div>
                        <Label className="label-handcraft">Pincode *</Label>
                        <Input
                          name="pincode"
                          value={addressForm.pincode}
                          onChange={handleAddressChange}
                          placeholder="400001"
                          data-testid="dialog-address-pincode"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="label-handcraft">Label</Label>
                      <div className="flex gap-2 mt-2">
                        {['Home', 'Work', 'Other'].map((label) => (
                          <Button
                            key={label}
                            type="button"
                            variant={addressForm.label === label ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setAddressForm(prev => ({ ...prev, label }))}
                            className={addressForm.label === label ? 'bg-charcoal text-cream' : ''}
                          >
                            {label}
                          </Button>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2 pt-4">
                      <Button
                        variant="outline"
                        onClick={() => setAddressDialogOpen(false)}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSaveAddress}
                        disabled={savingAddress}
                        className="flex-1 bg-charcoal text-cream hover:bg-charcoal/90"
                        data-testid="save-address-btn"
                      >
                        {savingAddress ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          'Save Address'
                        )}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </TabsContent>

            {/* Profile Tab */}
            <TabsContent value="profile">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-serif text-xl">Profile Information</CardTitle>
                  {!editing && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setEditing(true)}
                      data-testid="edit-profile"
                    >
                      <Edit2 className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="label-handcraft">Full Name</Label>
                      {editing ? (
                        <Input
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          data-testid="profile-name"
                        />
                      ) : (
                        <p className="text-charcoal">{customer.name}</p>
                      )}
                    </div>

                    <div>
                      <Label className="label-handcraft">Email</Label>
                      <p className="text-charcoal">{customer.email}</p>
                      <p className="text-xs text-muted-foreground mt-1">Email cannot be changed</p>
                    </div>

                    <div>
                      <Label className="label-handcraft">Phone</Label>
                      {editing ? (
                        <Input
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+91 98765 43210"
                          data-testid="profile-phone"
                        />
                      ) : (
                        <p className="text-charcoal">{customer.phone || '-'}</p>
                      )}
                    </div>

                    <div>
                      <Label className="label-handcraft">Member Since</Label>
                      <p className="text-charcoal">
                        {new Date(customer.created_at).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>

                  {editing && (
                    <div className="flex gap-3 mt-6 pt-6 border-t">
                      <Button
                        variant="outline"
                        onClick={() => setEditing(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSaveProfile}
                        disabled={saving}
                        className="bg-charcoal text-cream hover:bg-charcoal/90"
                        data-testid="save-profile"
                      >
                        {saving ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          'Save Changes'
                        )}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
};

export default CustomerAccountPage;
